// IndexedDB-backed StashStore for the offline-logout held-work stash (#809 Tier-3).
//
// The durable on-device store the offline stash persists through. IndexedDB,
// deliberately, because it must SURVIVE PowerSync's `disconnectAndClear({
// clearLocal: true })` (which wipes the synced mirror AND every PowerSync
// local-only table) and must hold a large backlog (the Sentry 1P incident was
// 7,119 ops — past localStorage's ~5 MB quota). One database, one object store,
// keyed by the per-user stash key the logic layer builds (held-work-offline-
// stash.ts). Thin on purpose: all merge / user-scoping / dedupe logic lives in
// the pure layer over the StashStore seam; this file is only the engine adapter.
//
// jsdom has no IndexedDB, so this adapter is exercised by the real browser at
// runtime, not by the jsdom unit suite (which drives the pure logic over an
// in-memory StashStore). It is kept minimal so that untested boundary is small,
// mirroring how the PowerSync singleton wiring is a thin seam over the tested
// pure cores.

import type { StashStore } from './held-work-offline-stash'

const DB_NAME = 'citycare_held_work_stash'
const STORE_NAME = 'stash'
const DB_VERSION = 1

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function run<T>(
  mode: IDBTransactionMode,
  body: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode)
        const request = body(tx.objectStore(STORE_NAME))
        tx.oncomplete = () => {
          db.close()
          resolve(request.result)
        }
        tx.onerror = () => {
          db.close()
          reject(tx.error)
        }
        tx.onabort = () => {
          db.close()
          reject(tx.error)
        }
      }),
  )
}

/**
 * Atomically read-modify-write `key` in ONE readwrite transaction (CAS): get the
 * current value, hand it to the synchronous `updater`, then put the result (or
 * delete the key when the updater returns `undefined`) — all before the transaction
 * commits. IndexedDB serializes readwrite transactions on a store, so two concurrent
 * `update`s on the same key apply one-after-another rather than lost-update — the
 * atomicity the pure layer's merge / shipped-id-removal relies on (Codex P2). The
 * `updater` MUST be synchronous; an `await` inside it would let the transaction
 * auto-commit before the put.
 */
function updateInTransaction(
  key: string,
  updater: (current: unknown) => unknown,
): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite')
        const store = tx.objectStore(STORE_NAME)
        const getRequest = store.get(key)
        getRequest.onsuccess = () => {
          const next = updater(
            getRequest.result === undefined ? undefined : getRequest.result,
          )
          if (next === undefined) store.delete(key)
          else store.put(next, key)
        }
        tx.oncomplete = () => {
          db.close()
          resolve()
        }
        tx.onerror = () => {
          db.close()
          reject(tx.error)
        }
        tx.onabort = () => {
          db.close()
          reject(tx.error)
        }
      }),
  )
}

/**
 * The production StashStore over IndexedDB. Returns `undefined` from `get` for an
 * absent key (the pure layer treats that as "nothing stashed"). `update` is the
 * atomic CAS primitive (see updateInTransaction). The whole offline stash is
 * best-effort — the caller (logout) swallows a failure and surfaces the existing
 * loud "held data" path rather than ever trapping a surveyor on a shared device.
 */
export function createIndexedDbStashStore(): StashStore {
  return {
    get: (key) =>
      run('readonly', (store) => store.get(key)).then((value) =>
        value === undefined ? undefined : value,
      ),
    update: (key, updater) => updateInTransaction(key, updater),
    delete: (key) =>
      run('readwrite', (store) => store.delete(key)).then(() => undefined),
  }
}
